import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import '../styles.css';

export default function MembresPage() {
  const { api, user, isAnimateur } = useAuth();
  const [membres, setMembres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtre, setFiltre] = useState('membre');
  const [selected, setSelected] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    api.get('/contacts', { params: { statut: filtre, search } })
      .then(r => setMembres(r.data))
      .catch(() => setMembres([]))
      .finally(() => setLoading(false));
  }, [filtre, search, api]);

  const filtered = membres.filter(m =>
    !search ||
    m.nom?.toLowerCase().includes(search.toLowerCase()) ||
    m.prenom?.toLowerCase().includes(search.toLowerCase()) ||
    m.societe?.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = filtered.reduce((acc, m) => {
    const letter = (m.nom?.[0] || '?').toUpperCase();
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(m);
    return acc;
  }, {});

  if (selected) return (
    <FicheMembre
      membre={selected}
      onBack={() => setSelected(null)}
      isAnimateur={isAnimateur}
      shareOpen={shareOpen}
      setShareOpen={setShareOpen}
    />
  );

  return (
    <div style={{ minHeight: '100vh' }}>
      <div style={{ padding: '12px 20px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1c1c1e' }}>
          Membres <span style={{ fontSize: 15, color: '#8e8e93', fontWeight: 400 }}>({filtered.length})</span>
        </h1>
      </div>

      <div className="mb-content" style={{ paddingTop: 0 }}>
        {/* Filtres animateur */}
        {isAnimateur && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, overflowX: 'auto', paddingBottom: 2 }}>
            {['membre','invite_0','invite_1','invite_2','attente_paiement','resilie'].map(f => (
              <button key={f} onClick={() => setFiltre(f)}
                className={`mb-btn mb-btn-pill ${filtre === f ? 'mb-btn-primary' : ''}`}
                style={filtre !== f ? { background: 'white', color: '#8e8e93', border: '0.5px solid #d1d1d6' } : {}}>
                {labelStatut(f)}
              </button>
            ))}
          </div>
        )}

        {/* Recherche */}
        <div style={{ background: 'white', borderRadius: 12, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8e8e93" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, color: '#1c1c1e', background: 'transparent' }}
            placeholder="Nom, prénom, société..." />
          {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8e8e93', fontSize: 18 }}>×</button>}
          <span style={{ fontSize: 11, color: '#95C11F', fontWeight: 600, whiteSpace: 'nowrap' }}>A → Z</span>
        </div>

        {loading ? (
          <div className="mb-loading"><div className="mb-spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="mb-empty mb-card">
            <div className="mb-empty-title">Aucun résultat</div>
            <div className="mb-empty-sub">Essayez un autre terme de recherche</div>
          </div>
        ) : (
          Object.keys(grouped).sort().map(letter => (
            <div key={letter}>
              <div className="mb-section-label">{letter}</div>
              {grouped[letter].map(m => (
                <div key={m.id} className="mb-member-card" style={{ marginBottom: 8 }} onClick={() => setSelected(m)}>
                  <div className="mb-member-avatar" style={{ background: stringToColor(m.nom) }}>
                    {m.photo_url ? <img src={m.photo_url} alt="" /> : `${m.prenom?.[0]||''}${m.nom?.[0]||''}`}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1c1c1e' }}>{m.nom} {m.prenom}</div>
                    <div style={{ fontSize: 12, color: '#8e8e93', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.societe || m.metier_represente}
                    </div>
                    <span className={`mb-badge ${badgeClass(m.statut)}`} style={{ marginTop: 3 }}>
                      {labelStatut(m.statut)}
                    </span>
                  </div>
                  <svg width="8" height="14" viewBox="0 0 8 14" fill="none" stroke="#c7c7cc" strokeWidth="2"><path d="M1 1l6 6-6 6"/></svg>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function FicheMembre({ membre, onBack, isAnimateur, shareOpen, setShareOpen }) {
  const initials = `${membre.prenom?.[0]||''}${membre.nom?.[0]||''}`.toUpperCase();

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Top bar */}
      <div style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#95C11F', fontSize: 15, fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer' }}>
          <svg width="10" height="16" viewBox="0 0 10 16" fill="none" stroke="#95C11F" strokeWidth="2.5"><path d="M8 2L2 8l6 6"/></svg>
          Membres
        </button>
        <button onClick={() => setShareOpen(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 20, background: '#f0f8e1', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#3B6D11' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B6D11" strokeWidth="2.5"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
          Partager
        </button>
      </div>

      <div className="mb-content" style={{ paddingTop: 0 }}>
        {/* Hero */}
        <div className="mb-hero mb-mb-md">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div className="mb-hero-avatar" style={{ background: stringToColor(membre.nom) }}>
              {membre.photo_url ? <img src={membre.photo_url} alt="" /> : initials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="mb-hero-name">{membre.prenom} {membre.nom}</div>
              <div className="mb-hero-sub">{membre.metier_represente || membre.poste}</div>
              <div className="mb-hero-company">{membre.societe}</div>
            </div>
          </div>
        </div>

        {/* Stats animateur */}
        {isAnimateur && (
          <div className="mb-card mb-mb-md" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '9px 14px', background: '#f0f8e1', display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3B6D11" strokeWidth="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#3B6D11', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Visible animateur uniquement</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1, background: '#f2f2f7' }}>
              {[
                { val: '52k€', lbl: 'CA généré', color: '#95C11F' },
                { val: '38k€', lbl: 'CA reçu', color: '#3C2618' },
                { val: '87%', lbl: 'Présence', color: '#1c1c1e' },
              ].map(s => (
                <div key={s.lbl} style={{ background: 'white', padding: '12px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <span style={{ fontSize: 17, fontWeight: 700, color: s.color }}>{s.val}</span>
                  <span style={{ fontSize: 10, color: '#8e8e93', textAlign: 'center' }}>{s.lbl}</span>
                </div>
              ))}
            </div>
            <div style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between' }}>
              <div><div style={{ fontSize: 11, color: '#8e8e93' }}>Parrain</div><div style={{ fontSize: 13, fontWeight: 500 }}>{membre.parrain_nom || '—'}</div></div>
              <div style={{ textAlign: 'right' }}><div style={{ fontSize: 11, color: '#8e8e93' }}>Fin adhésion</div><div style={{ fontSize: 13, fontWeight: 500, color: '#f59e0b' }}>{membre.date_fin_adhesion ? new Date(membre.date_fin_adhesion).toLocaleDateString('fr-FR') : '—'}</div></div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mb-action-grid mb-mb-md">
          {[
            { label: 'Merci', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#95C11F" strokeWidth="2.5"><path d="M20 12V22H4V12"/><path d="M22 7H2v5h20V7z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg> },
            { label: 'Tête-à-tête', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#95C11F" strokeWidth="2.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg> },
            { label: 'Reco', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#95C11F" strokeWidth="2.5"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg> },
          ].map(a => (
            <button key={a.label} className="mb-action-card">
              <div className="mb-action-ico">{a.icon}</div>
              <span className="mb-action-lbl">{a.label}</span>
            </button>
          ))}
        </div>

        {/* Coordonnées */}
        <div className="mb-card mb-mb-md">
          <div className="mb-card-title">Coordonnées</div>
          {[
            { label: 'Mobile', value: membre.mobile, href: `tel:${membre.mobile}`, link: true },
            { label: 'Email', value: membre.email, href: `mailto:${membre.email}`, link: true, small: true },
            { label: 'Adresse', value: membre.adresse_bureau },
            { label: 'Site web', value: membre.site_web, href: membre.site_web, link: true, small: true },
            { label: 'Membre depuis', value: membre.date_entree ? new Date(membre.date_entree).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—' },
          ].filter(r => r.value).map(r => (
            <div key={r.label} className="mb-info-row">
              <div className="mb-info-ico">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#95C11F" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>
              </div>
              <span className="mb-info-label">{r.label}</span>
              {r.link ? (
                <a href={r.href} className="mb-info-value mb-info-link" style={r.small ? { fontSize: 12 } : {}}>{r.value}</a>
              ) : (
                <span className="mb-info-value" style={r.small ? { fontSize: 12 } : {}}>{r.value}</span>
              )}
            </div>
          ))}
        </div>

        {/* Présentation */}
        {membre.description && (
          <div className="mb-card mb-mb-md">
            <div className="mb-card-title">Présentation</div>
            <p style={{ fontSize: 13, color: '#3c3c43', lineHeight: 1.6 }}>{membre.description}</p>
          </div>
        )}

        {/* Réseaux sociaux */}
        {(membre.lien_facebook || membre.lien_linkedin || membre.lien_instagram) && (
          <div className="mb-card mb-mb-md">
            <div className="mb-card-title">Réseaux sociaux</div>
            <div style={{ display: 'flex', justifyContent: 'space-around' }}>
              {membre.lien_facebook && <a href={membre.lien_facebook} target="_blank" rel="noreferrer" className="mb-social-btn" style={{ background: '#e8f0fe' }}><svg width="22" height="22" viewBox="0 0 24 24" fill="#1877f2"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg></a>}
              {membre.lien_linkedin && <a href={membre.lien_linkedin} target="_blank" rel="noreferrer" className="mb-social-btn" style={{ background: '#e8f4fb' }}><svg width="22" height="22" viewBox="0 0 24 24" fill="#0a66c2"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg></a>}
              {membre.lien_instagram && <a href={membre.lien_instagram} target="_blank" rel="noreferrer" className="mb-social-btn" style={{ background: '#fce4ec' }}><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#e1306c" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1.5" fill="#e1306c" stroke="none"/></svg></a>}
              <button className="mb-social-btn" style={{ background: '#fff3e0' }}><svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#ea4335"/><circle cx="12" cy="9" r="2.5" fill="white"/></svg></button>
            </div>
          </div>
        )}
      </div>

      {/* Modal partage */}
      {shareOpen && (
        <div onClick={() => setShareOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1c1c1e', textAlign: 'center', marginBottom: 16 }}>
              Partager la fiche de {membre.prenom} {membre.nom}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 16 }}>
              {[
                { label: 'Email', bg: '#e8f0fe', color: '#2563eb', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg> },
                { label: 'SMS', bg: '#f0f8e1', color: '#3B6D11', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#95C11F" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg> },
                { label: 'Copier le lien', bg: '#f5ede8', color: '#3C2618', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3C2618" strokeWidth="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg> },
              ].map(opt => (
                <button key={opt.label} onClick={() => { toast.success(`Partage par ${opt.label}`); setShareOpen(false); }}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer' }}>
                  <div style={{ width: 52, height: 52, borderRadius: 14, background: opt.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{opt.icon}</div>
                  <span style={{ fontSize: 12, fontWeight: 500, color: '#3c3c43' }}>{opt.label}</span>
                </button>
              ))}
            </div>
            <button onClick={() => setShareOpen(false)} style={{ width: '100%', padding: 14, borderRadius: 12, background: '#f2f2f7', border: 'none', fontSize: 15, fontWeight: 600, color: '#3C2618', cursor: 'pointer' }}>
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function labelStatut(s) {
  const map = { membre:'Membre actif', invite_0:'Invité 0', invite_1:'Invité 1', invite_2:'Invité 2', attente_paiement:'Attente paiement', resilie:'Résilié', exclu:'Exclu', contact:'Contact' };
  return map[s] || s;
}

function badgeClass(s) {
  if (s === 'membre') return 'mb-badge-green';
  if (s === 'resilie' || s === 'exclu') return 'mb-badge-red';
  if (s === 'attente_paiement') return 'mb-badge-blue';
  return 'mb-badge-orange';
}

function stringToColor(str) {
  const colors = ['#95C11F','#3C2618','#7a9e18','#2563eb','#7c3aed','#0891b2','#059669'];
  let hash = 0;
  for (let i = 0; i < (str||'').length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}
